import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import styled from "styled-components";
import { BackupUser } from "../../view/contents/backup/backup.user";
import BackupDeviceList from "../../view/contents/backup/backup.device.list";
import { fetchLoadClientLogin } from "../../api/auth";
import ModalCenterLayout from "../modal/modal.center.layout";
import ModifyModal from "../user/modal/modify.modal";
import DeleteModal from "../user/modal/delete.modal";

const Accordion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  .group {
    border: 1px solid #e3e9e8;
    border-radius: 0.8rem;
    overflow: hidden;
  }
  .group-header {
    display: flex;
    align-items: center;
    background: #f7fbfa;
  }
  button {
    cursor: pointer;
    font: inherit;
  }
  .group-toggle {
    display: flex;
    align-items: center;
    gap: 1.2rem;
    flex: 1;
    min-width: 0;
    padding: 1.8rem;
    border: 0;
    background: none;
    text-align: left;
    font-size: 1.4rem;
    overflow-wrap: anywhere;
  }
  .group-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
    padding: 1rem;
  }
  .group-actions button {
    background: white;
    border: 0;
    border-radius: 0.4rem;
    padding: 0.6rem 1rem;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
    color: white;
    font-weight: 400;
    img {
      width: 20px;
      height: 20px;
    }
    &.write_ {
      background-color: #4abbcc;
    }
    &.del_ {
      background-color: #ff4a48;
    }
  }
  .group-toggle:focus-visible {
    outline: 2px solid #30a690;
    outline-offset: -2px;
  }
  .arrow {
    display: inline-block;
    flex-shrink: 0;
    transition: transform 0.2s;
    transform: rotate(90deg);
    height: 20px;
    img {
      height: 100%;
    }
  }
  .arrow.open {
    transform: rotate(180deg);
  }
  .device-panel {
    padding: 1.6rem;
  }
  @media (prefers-reduced-motion: reduce) {
    .arrow {
      transition: none;
    }
  }
`;

// Keep the existing client-login → admin-device-list request sequence.
const GroupDevices = ({
  user,
  backupKind,
}: {
  user: BackupUser;
  backupKind: "file" | "db" | "image";
}) => {
  const [status, setStatus] = useState("loading");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setStatus("loading");
    fetchLoadClientLogin({
      companyCode: localStorage.getItem("companyCode") || "",
      userAccount: user.userAccount,
      password: user.password,
      deviceId: "",
    })
      .then(() => {
        if (active) setStatus("ready");
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [user.userAccount, user.password, retry]);

  if (status === "loading")
    return <div role="status">디바이스를 불러오는 중입니다...</div>;
  if (status === "error")
    return (
      <div role="alert">
        디바이스 목록을 불러오지 못했습니다.{" "}
        <button type="button" onClick={() => setRetry((value) => value + 1)}>
          다시 시도
        </button>
      </div>
    );
  return (
    <BackupDeviceList
      embedded
      backupKind={backupKind}
      targetUserId={user.userId}
      department={user.department}
    />
  );
};

interface Props {
  data: BackupUser[];
  isLoading: boolean;
  onUpdate: () => void;
  backupKind?: "file" | "db" | "image";
}

const UserDeviceAccordion = ({
  data,
  isLoading,
  onUpdate,
  backupKind = "file",
}: Props) => {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [modal, setModal] = useState<{
    type: "edit" | "delete";
    user: BackupUser;
  } | null>(null);
  const reducedMotion = useReducedMotion();
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current || isLoading || data.length === 0) return;
    initialized.current = true;
    setExpanded(new Set([data[0].userId]));
  }, [data, isLoading]);

  const toggle = (userId: number) =>
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  if (isLoading) return <div role="status">데이터를 불러오는 중입니다...</div>;
  if (!data.length) return <div>검색 결과가 없습니다.</div>;

  return (
    <Accordion>
      {data.map((user) => {
        const open = expanded.has(user.userId);
        return (
          <section className="group" key={user.userId}>
            <div className="group-header" onClick={() => toggle(user.userId)}>
              <button
                type="button"
                className="group-toggle"
                id={`group-${user.userId}`}
                aria-expanded={open}
                aria-controls={`devices-${user.userId}`}
              >
                <span
                  className={`arrow${open ? " open" : ""}`}
                  aria-hidden="true"
                >
                  <img src="/assets/left-arrow.png" alt="" />
                </span>
                <span>
                  <b>{user.userName || "미입력"}</b> ({user.department || "-"} |{" "}
                  {user.userAccount})
                </span>
              </button>
              <div className="group-actions">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setModal({ type: "edit", user });
                  }}
                  className="write_"
                >
                  <img src="/assets/new_design/write_icon.png" alt="" />
                  구성원 수정
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setModal({ type: "delete", user });
                  }}
                  className="del_"
                >
                  <img src="/assets/new_design/del_icon.png" alt="" />
                  구성원 삭제
                </button>
              </div>
            </div>
            <div
              id={`devices-${user.userId}`}
              role="region"
              aria-labelledby={`group-${user.userId}`}
            >
              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    key="devices"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: reducedMotion ? 0 : 0.2 }}
                    style={{ overflow: "hidden" }}
                  >
                    <div className="device-panel">
                      <GroupDevices user={user} backupKind={backupKind} />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </section>
        );
      })}
      {modal && (
        <ModalCenterLayout
          isVisible
          closedHandler={() => setModal(null)}
          size={modal.type === "edit" ? "m" : undefined}
        >
          {modal.type === "edit" ? (
            <ModifyModal
              user={modal.user}
              closedHandler={() => {
                setModal(null);
                onUpdate();
              }}
            />
          ) : (
            <DeleteModal
              selectedUsers={[modal.user]}
              closedHandler={() => setModal(null)}
              onDeleteSuccess={onUpdate}
            />
          )}
        </ModalCenterLayout>
      )}
    </Accordion>
  );
};

export default UserDeviceAccordion;
